import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { Building2, User } from "lucide-react";
import { PasswordField, PasswordChecklist } from "@/components/PasswordUI";
import { passwordValid, isValidEmail } from "@/lib/validators";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") === "candidate" ? "candidate" : "startup_employer");
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const mismatch = form.confirm.length > 0 && form.password !== form.confirm;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.name.trim().length < 2) return setErr("Please enter your full name.");
    if (!isValidEmail(form.email)) return setErr("Please enter a valid email address.");
    if (!passwordValid(form.password)) return setErr("Your password does not meet the requirements below.");
    if (form.password !== form.confirm) return setErr("Passwords do not match.");
    setBusy(true);
    try {
      const u = await register({ name: form.name.trim(), email: form.email.trim().toLowerCase(), password: form.password, role });
      toast.success("Account created");
      nav(u.role === "candidate" ? "/candidate" : "/employer");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  const input = "mt-1.5 w-full rounded-lg border border-white/12 bg-surface px-3.5 py-2.5 text-sm text-ink focus:border-azure focus:outline-none focus:ring-2 focus:ring-azure/40 transition-colors duration-200";

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-surface border-r border-white/10 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 h-80 w-80 rounded-full bg-emerald/10 blur-[100px]" aria-hidden />
        <Logo />
        <div className="relative">
          <h2 className="font-display font-bold tracking-tight text-3xl max-w-sm">Assemble the technical team your startup was meant to have.</h2>
          <p className="mt-4 text-muted max-w-sm">Join as a startup to find talent, or apply to be discovered as exceptional technical talent.</p>
        </div>
        <p className="label-mono text-faint relative">teamassembly.io</p>
      </div>
      <div className="flex items-center justify-center p-6 py-10">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="register-form">
          <div className="lg:hidden mb-8"><Logo /></div>
          <h1 className="font-display font-bold text-2xl mb-1">Create your account</h1>
          <p className="text-sm text-muted mb-6">Choose how you want to join Team Assembly.<span className="text-signal"> *</span></p>

          <div className="grid grid-cols-2 gap-2 mb-6">
            <button type="button" onClick={() => setRole("startup_employer")} data-testid="role-employer"
              className={`rounded-lg border p-3 text-left transition-colors duration-200 ${role === "startup_employer" ? "border-azure bg-azure/10" : "border-white/12 hover:border-white/25"}`}>
              <Building2 size={16} className="text-azure mb-1.5" />
              <p className="text-sm font-medium text-ink">Startup / Employer</p>
              <p className="text-[11px] text-faint">Find talent</p>
            </button>
            <button type="button" onClick={() => setRole("candidate")} data-testid="role-candidate"
              className={`rounded-lg border p-3 text-left transition-colors duration-200 ${role === "candidate" ? "border-azure bg-azure/10" : "border-white/12 hover:border-white/25"}`}>
              <User size={16} className="text-emerald mb-1.5" />
              <p className="text-sm font-medium text-ink">Talent / Candidate</p>
              <p className="text-[11px] text-faint">Apply to join</p>
            </button>
          </div>

          {err && <div className="mb-4 rounded-lg border border-signal/30 bg-signal/10 px-3 py-2 text-sm text-signal" data-testid="register-error">{err}</div>}

          <div className="mb-4">
            <label className="label-mono text-faint">Full name <span className="text-signal">*</span></label>
            <input data-testid="register-name" type="text" required value={form.name}
              onChange={(e) => set("name", e.target.value)} className={input} />
          </div>
          <div className="mb-4">
            <label className="label-mono text-faint">Email <span className="text-signal">*</span></label>
            <input data-testid="register-email" type="email" required value={form.email}
              onChange={(e) => set("email", e.target.value)} className={input}
              placeholder={role === "startup_employer" ? "you@yourcompany.com" : "you@email.com"} />
          </div>
          <div className="mb-1">
            <label className="label-mono text-faint">Password <span className="text-signal">*</span></label>
            <div className="mt-1.5"><PasswordField value={form.password} onChange={(e) => set("password", e.target.value)} testId="register-password" autoComplete="new-password" /></div>
          </div>
          <PasswordChecklist value={form.password} />
          <div className="mt-4 mb-4">
            <label className="label-mono text-faint">Confirm password <span className="text-signal">*</span></label>
            <div className="mt-1.5"><PasswordField value={form.confirm} onChange={(e) => set("confirm", e.target.value)} testId="register-confirm" autoComplete="new-password" /></div>
            {mismatch && <p className="mt-1.5 text-xs text-signal" data-testid="register-mismatch">Passwords do not match.</p>}
          </div>

          <button disabled={busy} data-testid="register-submit"
            className="mt-2 w-full rounded-full bg-azure py-3 text-sm font-medium text-white hover:bg-white hover:text-black transition-colors duration-200 disabled:opacity-50">
            {busy ? "Creating…" : "Create account"}
          </button>
          <p className="mt-6 text-sm text-muted text-center">
            Already registered? <Link to="/login" className="text-azure hover:underline">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
